import { DEFAULT_PAGE_SIZE, IntegrationSource, PAGE_SIZE_OPTIONS } from '@/constants';
import { queryKeys } from '@/hooks/queryKeys';
import useDebounce from '@/hooks/useDebounce';
import { useGetCourseStudents } from '@/hooks/useGetCourseStudents';
import { useGetCourseStudentsSummary } from '@/hooks/useGetCourseStudentsSummary';
import { useAppData } from '@/providers/AppDataProvider';
import { useSnackbar } from '@/providers/SnackbarProvider';
import AddIcon from '@/resources/images/icons-add.svg';
import SearchIcon from '@/resources/images/icons-search.svg';
import { ICourseStudent, IStudentSummaryResponse } from '@/types/views/student.types';
import { isEnrollmentTerminal } from '@/utilities';
import { openStudentCertificatePdf } from '@/utilities/openCertificatePdf';
import {
  Box,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { GridCallbackDetails, GridPaginationModel } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { ManageTrainingEvent, Theme } from 'klayowebshared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { track } from '../common/track';
import { useLocale } from '../providers/LocaleProvider';
import User1Icon from '../resources/images/icons-users-1.svg';
import { Button } from './Button';
import { ConfirmUnenrolStudentDialog } from './dialogs/ConfirmUnenrolStudentDialog';
import { LearningHistoryDialog } from './dialogs/LearningHistoryDialog';
import { SendReminderDialog } from './dialogs/SendReminderDialog';
import { SetEnrolmentDateDialog, SetEnrolmentDateMode } from './dialogs/SetEnrolmentDateDialog';
import { EnrolmentCardList } from './EnrolmentCardList';
import { StatCard } from './StatCard';
import { CourseStudentRow, CourseStudentsTable } from './table/CourseStudentsTable';
import { TablePlaceholder } from './TablePlaceholder';

type Props = {
  theme: typeof Theme;
  courseId?: string;
  courseName?: string;
  isTrainingManager?: boolean;
  onLearningHistory?: (row: CourseStudentRow) => void;
  enabled?: boolean;
};

// The five LearnUpon statuses PARTITION the enrolments: every enrolment lands in at most one of
// them (an unrecognised status lands in none).
const STATUS_CARDS: { key: keyof IStudentSummaryResponse['summary']; labelKey: string }[] = [
  { key: 'completed', labelKey: 'manage.completed' },
  { key: 'inProgress', labelKey: 'manage.inProgress' },
  { key: 'notStarted', labelKey: 'manage.notStarted' },
  { key: 'failed', labelKey: 'manage.failed' },
  { key: 'pending', labelKey: 'manage.pending' },
  { key: 'overdue', labelKey: 'manage.overdue' },
];

type StatusCard = (typeof STATUS_CARDS)[number];

// Overdue is NOT a sixth status. It cross-cuts the five above — a past-due failure is counted in
// both — so it sits apart from the partition with a caption saying so. Rendering it as a peer is
// what let "Failed 0" sit next to a row the Failed filter returned.
const OVERDUE_CARD: StatusCard = { key: 'overdue', labelKey: 'manage.overdue' };

const mapStudentToRow = (student: ICourseStudent): CourseStudentRow => ({
  // Server-supplied row key, not the employee id: a student who took this course more than once
  // returns one row per LearnUpon enrolment, which would collide in the grid's row map.
  id: student.rowKey,
  employeeId: student.employeeId,
  name: student.fullName,
  jobTitle: student.jobTitle,
  status: student.status,
  isOverdue: student.isOverdue,
  dueDate: student.dueDate ?? undefined,
  courseAccessExpiresAt: student.courseAccessExpiresAt ?? undefined,
  progress: student.percentageCompleted,
  enrolledDate: student.enrolledDate ?? undefined,
  completedDate: student.completedDate ?? undefined,
  learnUponEnrollmentId: student.learnUponEnrollmentId,
  learnUponInstance: student.learnUponInstance,
  integrationId: student.integrationId,
  integrationSource: student.integrationSource,
  canSendReminder: student.canSendReminder,
  enrollable: student.enrollable,
  courseEnrollmentId: student.courseEnrollmentId ?? undefined,
  canUnenrol: student.canUnenrol,
  employeeTrainingId: student.employeeTrainingId ?? undefined,
  learningPathEnrollmentId: student.learningPathEnrollmentId ?? undefined,
  integrationEnrollmentId: student.integrationEnrollmentId ?? undefined,
  hasCertificate: student.hasCertificate,
  canSetDates: student.canSetDates,
});

export const EnrolmentsTab = ({
  theme,
  courseId,
  courseName,
  isTrainingManager,
  onLearningHistory,
  enabled = true,
}: Props) => {
  const { translate } = useLocale();
  const muiTheme = useTheme();
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useAppData();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [searchText, setSearchText] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<StatusCard[]>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [unenrolRow, setUnenrolRow] = useState<CourseStudentRow | null>(null);
  const [historyRow, setHistoryRow] = useState<CourseStudentRow | null>(null);
  const [reminderRow, setReminderRow] = useState<CourseStudentRow | null>(null);
  const [dateDialog, setDateDialog] = useState<{
    mode: SetEnrolmentDateMode;
    row: CourseStudentRow;
  } | null>(null);
  const menuRow = useRef<CourseStudentRow | null>(null);

  const searchDebounce = useDebounce(searchText, 500);

  useEffect(() => {
    if (enabled) track(ManageTrainingEvent.EnrolmentsViewed, { courseName });
  }, [enabled]);

  useEffect(() => {
    if (searchDebounce) track(ManageTrainingEvent.EnrolmentsSearch, { courseName });
  }, [searchDebounce]);

  // isFetching, not isPending: with keepPreviousData the page-change fetch shows old rows under
  // MUI's loading overlay instead of blanking the grid (isPending is false once a placeholder shows).
  const { data, isFetching: isLoading } = useGetCourseStudents({
    courseId: courseId,
    statusFilter: selectedStatuses.length ? selectedStatuses.map((s) => s.key) : undefined,
    searchText: searchDebounce,
    paginationModel,
    enabled: isTrainingManager && enabled,
  });

  const { data: summary, isPending: isSummaryLoading } = useGetCourseStudentsSummary(
    courseId,
    searchDebounce,
    isTrainingManager && enabled
  );

  const totalCount = data?.students?.totalCount ?? 0;

  const students = useMemo(() => data?.students?.data?.map(mapStudentToRow) ?? [], [data]);

  const handleStatusCardClick = (status: StatusCard) => {
    const filterActivated = !selectedStatuses.includes(status);
    track(ManageTrainingEvent.EnrolmentsQuickFilter, {
      courseName,
      filterName: status.key,
      filterActivated,
    });
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationModelChange = (
    model: GridPaginationModel,
    _details?: GridCallbackDetails
  ) => {
    setPaginationModel(model);
  };

  const handleStudentAction = (e: React.MouseEvent, row: CourseStudentRow) => {
    menuRow.current = row;
    setMenuAnchor(e.currentTarget as HTMLElement);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const isAllowEnrollActions =
    user?.isAllowEnrollStudents && menuRow.current?.integrationSource !== IntegrationSource.Manual;
  const isAllowUnenrollActions =
    user?.isAllowUnenrollStudents &&
    menuRow.current?.integrationSource !== IntegrationSource.Manual;

  const isTerminalStatus = isEnrollmentTerminal(menuRow.current?.status);
  const isAllowReminder =
    isAllowEnrollActions && !isTerminalStatus && menuRow.current?.canSendReminder;
  const isAllowUnenrol =
    isAllowUnenrollActions && !isTerminalStatus && !!menuRow.current?.canUnenrol;

  const showViewCertificate = !!menuRow.current?.hasCertificate;

  return (
    <Stack gap={3}>
      {/* Search + Enrol button */}
      <Stack direction='row' justifyContent='space-between' alignItems='center' gap={2}>
        <TextField
          placeholder={translate('manage.courseStudents.searchStudent')}
          size='small'
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon width={20} height={20} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: '100%', sm: 320 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '24px',
            },
            flex: {
              xs: 1,
              sm: 'none',
            },
          }}
        />
        {user?.isAllowEnrollStudents ? (
          <Button
            variant='filled'
            label={translate('manage.enrollStudents')}
            startIcon={<AddIcon width={20} height={20} />}
            onClick={() => {
              track(ManageTrainingEvent.EnrolmentsEnrolClicked, { courseName });
              navigate(`/settings/learning/enroll-students?courseId=${courseId}`);
            }}
          />
        ) : null}
      </Stack>

      {/* Status summary cards — the five statuses partition the enrolments; Overdue cross-cuts them */}
      <Box>
        <Grid container spacing={2}>
          {STATUS_CARDS.map((status) => (
            <Grid item xs={6} sm={4} md={2} key={status.key}>
              <StatCard
                title={translate(status.labelKey)}
                stat={summary?.[status.key] ?? 0}
                loading={isSummaryLoading}
                selected={selectedStatuses.includes(status)}
                hasActiveSelection={selectedStatuses.length > 0}
                onClick={() => handleStatusCardClick(status)}
                sx={{ height: '100%', boxSizing: 'border-box', padding: '20px' }}
                statStyle={{
                  fontSize: '24px',
                  lineHeight: '32px',
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Enrolments list */}
      {isSmallScreen ? (
        <EnrolmentCardList
          rows={students}
          totalCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          translate={translate}
          isLoading={isLoading}
          onStudentAction={handleStudentAction}
        />
      ) : (
        <CourseStudentsTable
          rows={students}
          columns={[
            { type: CourseStudentsTable.columns.studentName },
            { type: CourseStudentsTable.columns.enrolmentDate },
            { type: CourseStudentsTable.columns.dueDate },
            { type: CourseStudentsTable.columns.courseExpiry },
            { type: CourseStudentsTable.columns.statusWithProgress },
            { type: CourseStudentsTable.columns.actions },
          ]}
          theme={theme}
          translate={translate}
          paper={false}
          pagination={true}
          showFooter={true}
          sortable={true}
          isLoading={isLoading}
          paginationMode='server'
          paginationModel={paginationModel}
          totalCount={totalCount}
          onPaginationModelChange={handlePaginationModelChange}
          noRowsMessage={translate('manage.courseStudents.noStudents')}
          keyword={searchDebounce}
          onStudentAction={handleStudentAction}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          noResultsComponent={
            <TablePlaceholder
              text={{
                primary: translate('manage.courseStudents.nothingToShow'),
                secondary: translate('manage.courseStudents.manageEnrolmentsHint'),
              }}
              icon={<User1Icon />}
            />
          }
          noRowsComponent={
            <TablePlaceholder
              text={{
                primary: translate('manage.courseStudents.nothingToShow'),
                secondary: translate('manage.courseStudents.manageEnrolmentsHint'),
              }}
              icon={<User1Icon />}
            />
          }
        />
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuRow.current?.canSetDates
          ? [
              <MenuItem
                key='set-due-date'
                onClick={() => {
                  setDateDialog({ mode: 'dueDate', row: menuRow.current! });
                  handleMenuClose();
                }}
              >
                {translate('manage.setEnrolmentDate.setDueDate')}
              </MenuItem>,
              <MenuItem
                key='set-expiry-date'
                onClick={() => {
                  setDateDialog({ mode: 'expiry', row: menuRow.current! });
                  handleMenuClose();
                }}
              >
                {translate('manage.setEnrolmentDate.setExpiryDate')}
              </MenuItem>,
            ]
          : null}
        <MenuItem
          onClick={() => {
            const row = menuRow.current;
            handleMenuClose();
            track(ManageTrainingEvent.EnrolmentsViewStudentHistory, { courseName });
            setHistoryRow(row);
            onLearningHistory?.(row!);
          }}
        >
          {translate('manage.courseStudents.learningHistory')}
        </MenuItem>
        {showViewCertificate ? (
          <MenuItem
            onClick={() => {
              const row = menuRow.current;
              const sku = data?.courseReferenceCode;
              handleMenuClose();
              if (!row || !sku) return;
              openStudentCertificatePdf({
                employeeId: row.employeeId,
                sku,
                enrollmentId: row.learnUponEnrollmentId,
                instance: row.learnUponInstance,
                onProcessing: () =>
                  showSnackbar(translate('myKlayo.certificateProcessing'), 'info'),
                onError: () => showSnackbar(translate('myKlayo.certificateUnavailable'), 'error'),
              });
            }}
          >
            {translate('manage.courseStudents.viewCertificate')}
          </MenuItem>
        ) : null}
        {isAllowReminder ? (
          <MenuItem
            onClick={() => {
              setReminderRow(menuRow.current);
              handleMenuClose();
            }}
          >
            {translate('manage.courseStudents.sendReminder')}
          </MenuItem>
        ) : null}
        {isAllowUnenrol ? (
          <MenuItem
            onClick={() => {
              setUnenrolRow(menuRow.current);
              handleMenuClose();
            }}
          >
            {translate('manage.courseStudents.unenrol')}
          </MenuItem>
        ) : null}
      </Menu>

      <SetEnrolmentDateDialog
        open={!!dateDialog}
        mode={dateDialog?.mode ?? 'dueDate'}
        studentName={dateDialog?.row.name}
        courseName={courseName}
        courseEnrollmentId={dateDialog?.row.courseEnrollmentId}
        dueDate={dateDialog?.row.dueDate}
        expiryDate={dateDialog?.row.courseAccessExpiresAt}
        theme={theme}
        onClose={() => setDateDialog(null)}
        onSuccess={() => {
          // Same parent-prefix invalidation as unenrol: a due-date change can flip a row's overdue
          // state, so the summary must refetch along with the list.
          queryClient.invalidateQueries({ queryKey: queryKeys.courseStudents.key });
        }}
      />

      {unenrolRow && (
        <ConfirmUnenrolStudentDialog
          row={unenrolRow}
          courseName={courseName}
          theme={theme}
          onClose={() => setUnenrolRow(null)}
          onSuccess={() => {
            // Invalidate by the parent prefix so both the students list and the summary refetch
            // regardless of their params. summary()/list() build a params object on the key, so a
            // no-arg call produces `{ courseId: undefined }` which never partial-matches the active
            // summary query (which carries the real courseId).
            queryClient.invalidateQueries({ queryKey: queryKeys.courseStudents.key });
            queryClient.invalidateQueries({ queryKey: queryKeys.trainingCourses.byId() });
          }}
        />
      )}

      <LearningHistoryDialog
        open={!!historyRow}
        onClose={() => setHistoryRow(null)}
        studentName={historyRow?.name}
        courseName={courseName}
        courseEnrollmentId={historyRow?.courseEnrollmentId}
        employeeTrainingId={historyRow?.employeeTrainingId}
        learningPathEnrollmentId={historyRow?.learningPathEnrollmentId}
        integrationEnrollmentId={historyRow?.integrationEnrollmentId}
        theme={theme}
      />

      <SendReminderDialog
        open={!!reminderRow}
        employeeId={reminderRow?.employeeId}
        studentName={reminderRow?.name}
        courseId={courseId}
        courseName={courseName}
        theme={theme}
        onClose={() => setReminderRow(null)}
        onSuccess={() => track(ManageTrainingEvent.EnrolmentsSendReminder, { courseName })}
      />
    </Stack>
  );
};
